import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Mail, Play, Pause, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { SequenceBuilderDialog } from "./SequenceBuilderDialog";
import { useAuth } from "@/hooks/useAuth";

interface Sequence {
  id: string;
  name: string;
  description: string;
  status: string;
  total_steps: number;
  active_leads: number;
  reply_rate: number;
}

interface SequencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SequencesDialog = ({ open, onOpenChange }: SequencesDialogProps) => {
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [loading, setLoading] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState<Sequence | null>(null);
  
  const { user, orgId } = useAuth();

  useEffect(() => {
    if (open) {
      loadSequences();
    }
  }, [open]);

  const loadSequences = async () => {
    setLoading(true);
    try {
      if (!user || !orgId) return;

      const { data, error } = await supabase
        .from('email_sequences')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const base = data || [];
      const ids = base.map(s => s.id);

      // Compute live stats so UI never shows stale/zero counters
      const sb: any = supabase;
      const [stepsRes, enrollRes]: any = await Promise.all([
        ids.length
          ? sb.from('sequence_steps').select('sequence_id').in('sequence_id', ids)
          : Promise.resolve({ data: [] }),
        ids.length
          ? sb.from('campaign_enrollments').select('id, sequence_id, status').in('sequence_id', ids)
          : Promise.resolve({ data: [] }),
      ]);

      const enrollments = (enrollRes.data || []) as Array<{ id: string; sequence_id: string; status: string }>;
      const enrollIds = enrollments.map(e => e.id);
      const enrollToSeq: Record<string, string> = {};
      enrollments.forEach(e => { enrollToSeq[e.id] = e.sequence_id; });

      const sendsRes: any = enrollIds.length
        ? await sb
            .from('campaign_email_sends')
            .select('enrollment_id, replied_at')
            .in('enrollment_id', enrollIds)
        : { data: [] };

      const stepCount: Record<string, number> = {};
      (stepsRes.data || []).forEach((r: any) => {
        stepCount[r.sequence_id] = (stepCount[r.sequence_id] || 0) + 1;
      });
      const activeLeads: Record<string, number> = {};
      enrollments.forEach((r) => {
        if (r.status === 'active')
          activeLeads[r.sequence_id] = (activeLeads[r.sequence_id] || 0) + 1;
      });
      const sendTotals: Record<string, { total: number; replied: number }> = {};
      (sendsRes.data || []).forEach((r: any) => {
        const seqId = enrollToSeq[r.enrollment_id];
        if (!seqId) return;
        const t = sendTotals[seqId] || { total: 0, replied: 0 };
        t.total += 1;
        if (r.replied_at) t.replied += 1;
        sendTotals[seqId] = t;
      });

      const enriched = base.map((s: any) => {
        const totals = sendTotals[s.id];
        const replyRate = totals && totals.total > 0
          ? Math.round((totals.replied / totals.total) * 100)
          : 0;
        return {
          ...s,
          total_steps: stepCount[s.id] ?? s.total_steps ?? 0,
          active_leads: activeLeads[s.id] ?? 0,
          reply_rate: replyRate,
        };
      });

      setSequences(enriched);
    } catch (error) {
      console.error("Error loading sequences:", error);
      toast.error("Failed to load sequences");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (sequenceId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'active' ? 'paused' : 'active';
    
    try {
      const { error } = await supabase
        .from('email_sequences')
        .update({ status: newStatus })
        .eq('id', sequenceId);

      if (error) throw error;
      toast.success(`Sequence ${newStatus === 'active' ? 'activated' : 'paused'}`);
      loadSequences();
    } catch (error) {
      console.error("Error updating sequence:", error);
      toast.error("Failed to update sequence");
    }
  };

  const handleDelete = async (sequenceId: string) => {
    if (!confirm("Are you sure you want to delete this sequence?")) return;

    try {
      const { error } = await supabase
        .from('email_sequences')
        .delete()
        .eq('id', sequenceId);

      if (error) throw error;
      toast.success("Sequence deleted");
      loadSequences();
    } catch (error) {
      console.error("Error deleting sequence:", error);
      toast.error("Failed to delete sequence");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'paused': return 'bg-yellow-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-muted';
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl">Email Sequences</DialogTitle>
              <Button
                onClick={() => {
                  setSelectedSequence(null);
                  setBuilderOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Sequence
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : sequences.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No sequences yet</h3>
                  <p className="text-muted-foreground mb-4">
                    Create your first email sequence to automate your outreach
                  </p>
                  <Button
                    onClick={() => {
                      setSelectedSequence(null);
                      setBuilderOpen(true);
                    }}
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Create Sequence
                  </Button>
                </CardContent>
              </Card>
            ) : (
              sequences.map((sequence) => (
                <Card key={sequence.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CardTitle>{sequence.name}</CardTitle>
                          <Badge className={getStatusColor(sequence.status)} variant="default">
                            {sequence.status}
                          </Badge>
                        </div>
                        {sequence.description && (
                          <p className="text-sm text-muted-foreground">{sequence.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleToggleStatus(sequence.id, sequence.status)}
                        >
                          {sequence.status === 'active' ? (
                            <Pause className="w-4 h-4" />
                          ) : (
                            <Play className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedSequence(sequence);
                            setBuilderOpen(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(sequence.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-muted-foreground">Steps</div>
                        <div className="font-semibold">{sequence.total_steps}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Active Leads</div>
                        <div className="font-semibold">{sequence.active_leads}</div>
                      </div>
                      <div>
                        <div className="text-muted-foreground">Reply Rate</div>
                        <div className="font-semibold">{sequence.reply_rate}%</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <SequenceBuilderDialog
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        sequence={selectedSequence}
        onSaved={() => {
          loadSequences();
          setBuilderOpen(false);
        }}
      />
    </>
  );
};
